import * as React from 'react';

import { useVehicles } from '../../../hooks/queries/useVehicles';
import { DashedAddButton, SettingsTabLoading } from '../common';

import DraftVehicleCard from './DraftVehicleCard';
import VehicleCard from './VehicleCard';

/**
 * "Possessions" settings tab. Renders the family's vehicles — each card persists
 * its own edits inline, and "Add vehicle" appends an unsaved draft that persists
 * only once a field is filled, so this container just tracks the open drafts.
 */
const PossessionsTab = () => {
  const { vehicles, loading } = useVehicles();

  const [draftIds, setDraftIds] = React.useState<number[]>([]);
  const nextDraftId = React.useRef(0);
  const addDraft = React.useCallback(() => {
    setDraftIds((ids) => [...ids, nextDraftId.current++]);
  }, []);
  const removeDraft = React.useCallback((id: number) => {
    setDraftIds((ids) => ids.filter((draftId) => draftId !== id));
  }, []);

  if (loading && vehicles.length === 0) {
    return <SettingsTabLoading />;
  }

  return (
    // [overflow-anchor:none]: see CollapsibleSettingsCard — the cards' siblings
    // (header, add button) must not become scroll anchors either.
    <div className="flex max-w-[736px] flex-col gap-5 [overflow-anchor:none]">
      <div className="flex flex-col gap-0.5">
        <p className="text-primary text-xl font-semibold">Vehicles</p>
        <p className="text-tertiary text-sm">Vehicles registered to your loved ones.</p>
      </div>
      {vehicles.map((vehicle) => (
        <VehicleCard key={vehicle.id} vehicle={vehicle} />
      ))}
      {draftIds.map((id) => (
        <DraftVehicleCard key={`draft-${id}`} onCreated={() => removeDraft(id)} onDiscard={() => removeDraft(id)} />
      ))}
      {/* Figma 4227:125516 — 398×64 dashed pill, 16px radius, app-bg fill. */}
      <DashedAddButton
        size="lg"
        onClick={addDraft}
        className="bg-app hover:bg-secondary_hover h-16 w-full max-w-[398px] rounded-2xl"
      >
        Add vehicle
      </DashedAddButton>
    </div>
  );
};

export default PossessionsTab;
